/**
 * Ragdoll - Ergonomic figure for chair testing
 * Ported from ergoDoll.java (jBullet → Rapier.js), rebuilt 2026-07 as a
 * Euphoria-mannequin-style figure (NaturalMotion / GTA ragdoll look):
 * sphere neck nestled between broad shoulders with a pill head on top,
 * torso of three stacked rounded segments (chest widest), tapered limbs,
 * and flat paddle hands/feet (cuboids).
 *
 * SCALE: the app world is 1 unit = 100mm (sketch px = mm, world = px/100).
 * Default dimensions give a ~16.3-unit (~1.63m) adult. `scale` is a user
 * multiplier (Bigger/Smaller) and rebuilds the whole body.
 *
 * SKELETON + POSES: the figure is defined as a joint tree (SKELETON) and
 * built by forward kinematics from the pelvis, so a pose is just one
 * angle per joint (POSES.standing / POSES.seated). The same body-local
 * anchors feed both the FK placement and the Rapier joints, so the
 * spawned pose is exactly the joints' rest configuration.
 *
 * SIGN CONVENTION: the figure is built FACING +X (the direction the
 * side-profile chair faces); the left-right axis is Z. Every joint is a
 * revolute about +Z (sagittal flexion). Rapier measures the joint angle
 * as the child's rotation relative to the parent about that axis, with 0
 * where both frames coincide — so for a segment HANGING BELOW its joint
 * (legs, arms), positive angle swings it FORWARD (+X); for a segment
 * rising ABOVE its joint (spine, chest, head), positive angle leans it
 * BACKWARD. Knees therefore fold negative (calf backward), elbows
 * positive (forearm forward).
 *
 * MASS: totalMass (60kg) distributed anthropometrically per part.
 */

import * as THREE from 'three';

// Single flat color for the whole figure: warm white, reads against the
// pure-white chair panels
const FIGURE_COLOR = 0xEDECE8;

const Z_AXIS = { x: 0, y: 0, z: 1 };

/** Chair bounding box in the physics (pivot-local) frame */
export function chairLocalBox(chairMeshes = []) {
    const box = new THREE.Box3();
    for (const mesh of chairMeshes || []) {
        if (!mesh.geometry) continue;
        mesh.updateMatrix();
        if (!mesh.geometry.boundingBox) mesh.geometry.computeBoundingBox();
        // LOCAL matrix: physics runs in the pivot-local frame - the view
        // rotation (pivot group) must not leak in
        box.union(mesh.geometry.boundingBox.clone().applyMatrix4(mesh.matrix));
    }
    return box;
}

export class Ragdoll {
    // Body part enumeration (17 parts, Euphoria-mannequin configuration:
    // a sphere NECK nestled on the chest with a pill HEAD on top)
    static BodyPart = {
        PELVIS: 0,
        SPINE: 1,        // belly segment
        CHEST: 2,        // broad upper torso, carries the shoulders
        HEAD: 3,         // pill, rides on the neck sphere
        THIGH_L: 4,
        CALF_L: 5,
        FOOT_L: 6,
        THIGH_R: 7,
        CALF_R: 8,
        FOOT_R: 9,
        UPPER_ARM_L: 10,
        FOREARM_L: 11,
        HAND_L: 12,
        UPPER_ARM_R: 13,
        FOREARM_R: 14,
        HAND_R: 15,
        NECK: 16,        // sphere between the shoulders
        COUNT: 17
    };

    constructor(physicsWorld, scene) {
        this.physicsWorld = physicsWorld;
        this.scene = scene;
        this.RAPIER = physicsWorld.RAPIER;
        this.world = physicsWorld.world;

        // Storage arrays (re-initialized on every (re)build)
        this.bodies = new Array(Ragdoll.BodyPart.COUNT);
        this.meshes = new Array(Ragdoll.BodyPart.COUNT);
        this.joints = [];
        this.colliders = new Array(Ragdoll.BodyPart.COUNT);
        this.savedCollisionGroups = new Array(Ragdoll.BodyPart.COUNT);

        // Physical properties
        this.scale = 1.0;       // User multiplier (Bigger/Smaller)
        // Nominal 60kg adult. Paired with the chair compound body's
        // explicit ~8kg (see PhysicsWorld.createCompoundDynamicBody).
        this.totalMass = 60.0;

        const P = Ragdoll.BodyPart;

        // Part shapes in WORLD UNITS (1 unit = 100mm), unscaled.
        // capsule: {len (cylinder length), r} along local Y
        // box: {hx, hy, hz} half-extents (feet long in X, hands long in Y)
        //
        // AGE-BASED PROPORTIONS (ported from ergoDoll.buildProportions):
        // the Java doll blended an ADULT table into a TODDLER table as the
        // figure shrank - big head, short chubby legs, round torso. The
        // adult shapes below are the Greg-approved figure; the child
        // shapes apply the Java tables' child/adult ratios per part
        // (e.g. head len 0.25/0.13, thigh len 0.19/0.28) to those adult
        // dims. partShapes holds the CURRENT blend - see _updateBlend().
        const cap = (len, r) => ({ type: 'capsule', len, r });
        this.adultShapes = {
            [P.PELVIS]: cap(1.1, 1.2),
            [P.SPINE]: cap(1.7, 1.05),
            [P.CHEST]: cap(2.4, 1.4),
            // Real-person proportions: head ~16cm wide x ~24cm tall,
            // neck ~11cm across (1 unit = 100mm)
            [P.NECK]: cap(0.05, 0.55), // small sphere in the shoulders
            [P.HEAD]: cap(0.8, 0.8),   // pill on top
            [P.THIGH_L]: cap(4.7, 0.75), [P.THIGH_R]: cap(4.7, 0.75),
            [P.CALF_L]: cap(4.6, 0.5), [P.CALF_R]: cap(4.6, 0.5),
            [P.FOOT_L]: { type: 'box', hx: 0.9, hy: 0.175, hz: 0.35 },
            [P.FOOT_R]: { type: 'box', hx: 0.9, hy: 0.175, hz: 0.35 },
            [P.UPPER_ARM_L]: cap(2.7, 0.5), [P.UPPER_ARM_R]: cap(2.7, 0.5),
            [P.FOREARM_L]: cap(2.5, 0.34), [P.FOREARM_R]: cap(2.5, 0.34),
            [P.HAND_L]: { type: 'box', hx: 0.1, hy: 0.5, hz: 0.25 },
            [P.HAND_R]: { type: 'box', hx: 0.1, hy: 0.5, hz: 0.25 }
        };
        this.childShapes = {
            [P.PELVIS]: cap(2.08, 2.18),  // toddlers are mostly torso
            [P.SPINE]: cap(1.7, 1.92),
            [P.CHEST]: cap(2.4, 2.56),
            [P.NECK]: cap(0.02, 0.22),    // barely any neck
            [P.HEAD]: cap(1.54, 1.72),    // head ~25% of standing height
            [P.THIGH_L]: cap(3.2, 0.95), [P.THIGH_R]: cap(3.2, 0.95),
            [P.CALF_L]: cap(3.35, 0.96), [P.CALF_R]: cap(3.35, 0.96),
            // arms/feet widened past the Java ratios - babies are chubby,
            // and the straight ratio translation read as spindly
            [P.FOOT_L]: { type: 'box', hx: 0.6, hy: 0.26, hz: 0.45 },
            [P.FOOT_R]: { type: 'box', hx: 0.6, hy: 0.26, hz: 0.45 },
            [P.UPPER_ARM_L]: cap(2.45, 0.58), [P.UPPER_ARM_R]: cap(2.45, 0.58),
            [P.FOREARM_L]: cap(2.4, 0.46), [P.FOREARM_R]: cap(2.4, 0.46),
            [P.HAND_L]: { type: 'box', hx: 0.09, hy: 0.38, hz: 0.2 },
            [P.HAND_R]: { type: 'box', hx: 0.09, hy: 0.38, hz: 0.2 }
        };
        this.partShapes = {};
        this._blendT = -1; // force first _updateBlend

        // Anthropometric mass fractions of total body weight (sums ~1.0);
        // adjacent-body ratios kept within ~7:1 (solver jitter guidance)
        this.massFractions = {
            [P.PELVIS]: 0.14,
            [P.SPINE]: 0.12,
            [P.CHEST]: 0.22,
            [P.NECK]: 0.02,
            [P.HEAD]: 0.06,
            [P.THIGH_L]: 0.10, [P.THIGH_R]: 0.10,
            [P.CALF_L]: 0.048, [P.CALF_R]: 0.048,
            [P.FOOT_L]: 0.015, [P.FOOT_R]: 0.015,
            [P.UPPER_ARM_L]: 0.03, [P.UPPER_ARM_R]: 0.03,
            [P.FOREARM_L]: 0.017, [P.FOREARM_R]: 0.017,
            [P.HAND_L]: 0.007, [P.HAND_R]: 0.007
        };

        // Joint tree: built by FK from the pelvis. Anchors are BODY-LOCAL
        // FUNCTIONS of the blended shape table (unscaled - scaled at
        // build time), so joints stay on the body as the age-proportion
        // blend reshapes the parts. limits/rests are joint angles in
        // radians per the sign convention in the header.
        // tone: [stiffness, damping] for the weak muscle motor.
        const D = (id) => this.partShapes[id];
        this.SKELETON = [
            // torso chain (positive = lean back). Backward range is TIGHT
            // (the figure shouldn't fold backwards over a seat back),
            // forward stays loose - people slump forwards, not backwards.
            { name: 'spine', parent: P.PELVIS, child: P.SPINE,
              p: () => [0, D(P.PELVIS).len / 2 + 0.1, 0], c: () => [0, -D(P.SPINE).len / 2, 0],
              limits: [-0.61, 0.1], rest: { standing: 0, seated: 0.08 }, tone: [60, 10] },
            { name: 'chest', parent: P.SPINE, child: P.CHEST,
              p: () => [0, D(P.SPINE).len / 2, 0], c: () => [0, -D(P.CHEST).len / 2, 0],
              limits: [-0.44, 0.09], rest: { standing: 0, seated: 0.05 }, tone: [60, 10] },
            // neck sphere nestled ON the chest dome (anchor rides up the
            // dome at 0.82*chest radius so the sphere emerges without
            // "turtling" into the chest). TIGHT limits + firm tone: the
            // head group stays facing up.
            { name: 'neck', parent: P.CHEST, child: P.NECK,
              p: () => [0, D(P.CHEST).len / 2 + 0.82 * D(P.CHEST).r, 0],
              c: () => [0, -0.64 * D(P.NECK).r, 0],
              limits: [-0.2, 0.15], rest: { standing: 0, seated: -0.1 }, tone: [50, 8] },
            // pill head rides on the neck sphere, pivot near its base so
            // it nods about the neck
            { name: 'head', parent: P.NECK, child: P.HEAD,
              p: () => [0, 0.64 * D(P.NECK).r, 0],
              c: () => [0, -(D(P.HEAD).len / 2 + 0.75 * D(P.HEAD).r), 0],
              limits: [-0.35, 0.25], rest: { standing: 0, seated: -0.15 }, tone: [40, 6] },
            // legs (positive = thigh swings forward; knee folds NEGATIVE)
            { name: 'hipL', parent: P.PELVIS, child: P.THIGH_L,
              p: () => [0, -0.55 * D(P.PELVIS).len, -0.67 * D(P.PELVIS).r],
              c: () => [0, D(P.THIGH_L).len / 2, 0],
              limits: [-0.26, 1.83], rest: { standing: 0, seated: 1.5 }, tone: [40, 8] },
            { name: 'kneeL', parent: P.THIGH_L, child: P.CALF_L,
              p: () => [0, -D(P.THIGH_L).len / 2, 0], c: () => [0, D(P.CALF_L).len / 2, 0],
              limits: [-2.44, 0.0], rest: { standing: -0.03, seated: -1.45 }, tone: [25, 5] },
            { name: 'ankleL', parent: P.CALF_L, child: P.FOOT_L,
              p: () => [0, -D(P.CALF_L).len / 2, 0],
              c: () => [-0.61 * D(P.FOOT_L).hx, D(P.FOOT_L).hy, 0],
              limits: [-0.5, 0.5], rest: { standing: 0, seated: -0.05 }, tone: [10, 2] },
            { name: 'hipR', parent: P.PELVIS, child: P.THIGH_R,
              p: () => [0, -0.55 * D(P.PELVIS).len, 0.67 * D(P.PELVIS).r],
              c: () => [0, D(P.THIGH_R).len / 2, 0],
              limits: [-0.26, 1.83], rest: { standing: 0, seated: 1.5 }, tone: [40, 8] },
            { name: 'kneeR', parent: P.THIGH_R, child: P.CALF_R,
              p: () => [0, -D(P.THIGH_R).len / 2, 0], c: () => [0, D(P.CALF_R).len / 2, 0],
              limits: [-2.44, 0.0], rest: { standing: -0.03, seated: -1.45 }, tone: [25, 5] },
            { name: 'ankleR', parent: P.CALF_R, child: P.FOOT_R,
              p: () => [0, -D(P.CALF_R).len / 2, 0],
              c: () => [-0.61 * D(P.FOOT_R).hx, D(P.FOOT_R).hy, 0],
              limits: [-0.5, 0.5], rest: { standing: 0, seated: -0.05 }, tone: [10, 2] },
            // arms (positive = arm swings forward; elbow folds POSITIVE)
            { name: 'shoulderL', parent: P.CHEST, child: P.UPPER_ARM_L,
              p: () => [0, D(P.CHEST).len / 2 - 0.25,
                  -(D(P.CHEST).r + D(P.UPPER_ARM_L).r + 0.05)],
              c: () => [0, D(P.UPPER_ARM_L).len / 2, 0],
              limits: [-0.7, 2.97], rest: { standing: 0.05, seated: 0.26 }, tone: [12, 3] },
            { name: 'elbowL', parent: P.UPPER_ARM_L, child: P.FOREARM_L,
              p: () => [0, -D(P.UPPER_ARM_L).len / 2, 0], c: () => [0, D(P.FOREARM_L).len / 2, 0],
              limits: [-0.02, 2.53], rest: { standing: 0.17, seated: 0.5 }, tone: [8, 2] },
            { name: 'wristL', parent: P.FOREARM_L, child: P.HAND_L,
              p: () => [0, -D(P.FOREARM_L).len / 2, 0], c: () => [0, D(P.HAND_L).hy, 0],
              limits: [-0.44, 0.44], rest: { standing: 0, seated: 0 }, tone: [4, 1] },
            { name: 'shoulderR', parent: P.CHEST, child: P.UPPER_ARM_R,
              p: () => [0, D(P.CHEST).len / 2 - 0.25,
                  D(P.CHEST).r + D(P.UPPER_ARM_R).r + 0.05],
              c: () => [0, D(P.UPPER_ARM_R).len / 2, 0],
              limits: [-0.7, 2.97], rest: { standing: 0.05, seated: 0.26 }, tone: [12, 3] },
            { name: 'elbowR', parent: P.UPPER_ARM_R, child: P.FOREARM_R,
              p: () => [0, -D(P.UPPER_ARM_R).len / 2, 0], c: () => [0, D(P.FOREARM_R).len / 2, 0],
              limits: [-0.02, 2.53], rest: { standing: 0.17, seated: 0.5 }, tone: [8, 2] },
            { name: 'wristR', parent: P.FOREARM_R, child: P.HAND_R,
              p: () => [0, -D(P.FOREARM_R).len / 2, 0], c: () => [0, D(P.HAND_R).hy, 0],
              limits: [-0.44, 0.44], rest: { standing: 0, seated: 0 }, tone: [4, 1] }
        ];

        // State
        this.visible = true;
        this.isSitting = false;
        this.currentPose = 'standing';
        this.position = { x: 0, y: 2, z: 0 };
    }

    /**
     * Refresh partShapes from the age-proportion blend (ported from
     * ergoDoll.buildProportions): full ADULT at scale >= 1, full TODDLER
     * at scale <= 0.3, linear blend between - so a shrinking figure gets
     * a relatively bigger head, shorter chubbier legs and rounder torso,
     * matching average human proportions at different ages.
     */
    _updateBlend() {
        const t = Math.max(0, Math.min(1, (this.scale - 0.3) / 0.7));
        if (t === this._blendT) return;
        this._blendT = t;
        for (let i = 0; i < Ragdoll.BodyPart.COUNT; i++) {
            const a = this.adultShapes[i], c = this.childShapes[i];
            const out = { type: a.type };
            for (const k of Object.keys(a)) {
                if (k !== 'type') out[k] = c[k] * (1 - t) + a[k] * t;
            }
            this.partShapes[i] = out;
        }
    }

    /**
     * Forward kinematics: per-part {pos, quat} for a named pose, with the
     * pelvis at the origin (identity rotation). Scaled by this.scale.
     */
    computePose(poseName = 'standing') {
        this._updateBlend();
        const s = this.scale;
        const T = new Array(Ragdoll.BodyPart.COUNT);
        T[Ragdoll.BodyPart.PELVIS] = {
            pos: new THREE.Vector3(0, 0, 0),
            quat: new THREE.Quaternion()
        };
        for (const j of this.SKELETON) {
            const parent = T[j.parent];
            const angle = j.rest[poseName] ?? 0;
            // Child frame = parent frame rotated by `angle` about the
            // joint's +Z axis (matches Rapier's revolute zero-angle
            // convention: frames coincide at angle 0)
            const quat = parent.quat.clone().multiply(
                new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 0, 1), angle));
            const pa = j.p(), ca = j.c();
            const jointWorld = new THREE.Vector3(pa[0] * s, pa[1] * s, pa[2] * s)
                .applyQuaternion(parent.quat).add(parent.pos);
            const pos = jointWorld.sub(
                new THREE.Vector3(ca[0] * s, ca[1] * s, ca[2] * s).applyQuaternion(quat));
            T[j.child] = { pos, quat };
        }
        return T;
    }

    /** Half-extent of a part along a world axis, given its rotation */
    _halfExtent(partId, quat, axis) {
        const s = this.scale;
        const shape = this.partShapes[partId];
        if (shape.type === 'box') {
            const ex = new THREE.Vector3(1, 0, 0).applyQuaternion(quat);
            const ey = new THREE.Vector3(0, 1, 0).applyQuaternion(quat);
            const ez = new THREE.Vector3(0, 0, 1).applyQuaternion(quat);
            return s * (shape.hx * Math.abs(ex[axis]) +
                shape.hy * Math.abs(ey[axis]) + shape.hz * Math.abs(ez[axis]));
        }
        const ey = new THREE.Vector3(0, 1, 0).applyQuaternion(quat);
        return s * ((shape.len / 2) * Math.abs(ey[axis]) + shape.r);
    }

    /** AABB of a set of FK transforms (or live bodies) */
    _boundsOf(getTransform) {
        const b = { minX: Infinity, maxX: -Infinity, minY: Infinity, maxY: -Infinity, minZ: Infinity, maxZ: -Infinity };
        for (let i = 0; i < Ragdoll.BodyPart.COUNT; i++) {
            const t = getTransform(i);
            if (!t) continue;
            const hx = this._halfExtent(i, t.quat, 'x');
            const hy = this._halfExtent(i, t.quat, 'y');
            const hz = this._halfExtent(i, t.quat, 'z');
            b.minX = Math.min(b.minX, t.pos.x - hx);
            b.maxX = Math.max(b.maxX, t.pos.x + hx);
            b.minY = Math.min(b.minY, t.pos.y - hy);
            b.maxY = Math.max(b.maxY, t.pos.y + hy);
            b.minZ = Math.min(b.minZ, t.pos.z - hz);
            b.maxZ = Math.max(b.maxZ, t.pos.z + hz);
        }
        return b;
    }

    /**
     * Actual axis-aligned bounds of the figure from its live body poses
     * @returns {{minX,maxX,minY,maxY,minZ,maxZ}}
     */
    getBodyBounds() {
        return this._boundsOf(i => {
            const body = this.bodies[i];
            if (!body) return null;
            const t = body.translation();
            const r = body.rotation();
            return {
                pos: new THREE.Vector3(t.x, t.y, t.z),
                quat: new THREE.Quaternion(r.x, r.y, r.z, r.w)
            };
        });
    }

    /**
     * Human-readable age estimate from the figure's standing height,
     * using typical stature-for-age (the same idea the age-proportion
     * blend is built on). Returns e.g. "6 months", "≈4 yrs", "adult".
     */
    getAgeDescription() {
        const h = this.getStandingHeight() / 10; // world units -> metres
        // [stature m, age yrs] typical growth curve
        const curve = [
            [0.50, 0], [0.75, 1], [0.87, 2], [0.96, 3], [1.03, 4],
            [1.10, 5], [1.16, 6], [1.28, 8], [1.38, 10], [1.49, 12],
            [1.63, 14], [1.73, 16], [1.77, 18]
        ];
        if (h >= 1.77) return 'adult';
        if (h <= 0.50) return 'newborn';
        let age = 18;
        for (let i = 1; i < curve.length; i++) {
            if (h <= curve[i][0]) {
                const [h0, a0] = curve[i - 1], [h1, a1] = curve[i];
                age = a0 + (a1 - a0) * (h - h0) / (h1 - h0);
                break;
            }
        }
        if (age < 1) return `${Math.max(1, Math.round(age * 12))} months`;
        return `≈${Math.round(age)} yrs`;
    }

    /** Standing height (ground to head top) in world units */
    getStandingHeight() {
        const T = this.computePose('standing');
        const b = this._boundsOf(i => T[i]);
        return b.maxY - b.minY;
    }

    /** Pelvis-centre Y that puts the feet exactly on the ground */
    standingPelvisY(groundY) {
        const T = this.computePose('standing');
        const b = this._boundsOf(i => T[i]);
        return groundY - b.minY + 0.05;
    }

    /**
     * Put every body to sleep: the figure STANDS (even while the
     * simulation runs) until something touches or grabs it - then it
     * wakes and behaves as a ragdoll. This is how the standing spawn
     * stays upright without balance control.
     */
    sleepAll() {
        for (const body of this.bodies) {
            body?.sleep?.();
        }
    }

    /**
     * Rigidly translate the whole figure (all bodies + meshes), zeroing
     * velocities - used for exact spawn placement and the paused-physics
     * carry drag.
     */
    translateAll(dx, dy, dz) {
        for (let i = 0; i < Ragdoll.BodyPart.COUNT; i++) {
            const body = this.bodies[i];
            if (!body) continue;
            const t = body.translation();
            body.setTranslation({ x: t.x + dx, y: t.y + dy, z: t.z + dz }, true);
            body.setLinvel({ x: 0, y: 0, z: 0 }, true);
            body.setAngvel({ x: 0, y: 0, z: 0 }, true);
            if (this.meshes[i]) {
                this.meshes[i].position.set(t.x + dx, t.y + dy, t.z + dz);
            }
        }
        this.position.x += dx;
        this.position.y += dy;
        this.position.z += dz;
    }

    /**
     * Initialize ragdoll (async for consistency)
     */
    async init(x = 0, y = 11, z = 0) {
        return this.create(x, y, z);
    }

    /**
     * Create the ragdoll at a position (pelvis centre) in a named pose
     * ('standing' | 'seated'), built by FK from the skeleton.
     */
    create(x = 0, y = 11, z = 0, pose = 'standing') {
        this.position = { x, y, z };
        this.currentPose = pose;

        // Fresh storage (create may run after a dispose)
        this.bodies = new Array(Ragdoll.BodyPart.COUNT);
        this.meshes = new Array(Ragdoll.BodyPart.COUNT);
        this.joints = [];
        this.colliders = new Array(Ragdoll.BodyPart.COUNT);
        this.savedCollisionGroups = new Array(Ragdoll.BodyPart.COUNT);

        const transforms = this.computePose(pose);
        const offset = new THREE.Vector3(x, y, z);
        for (let i = 0; i < Ragdoll.BodyPart.COUNT; i++) {
            const t = transforms[i];
            this.createBodyPart(i, t.pos.clone().add(offset), t.quat);
        }
        this.createJoints(pose);

        console.log(`Ragdoll created at (${x.toFixed(1)}, ${y.toFixed(1)}, ${z.toFixed(1)}) ` +
            `pose=${pose}, height ~${this.getStandingHeight().toFixed(1)} units ` +
            `(~${Math.round(this.getStandingHeight() * 100)}mm), mass ${(this.totalMass * Math.pow(this.scale, 2.3)).toFixed(1)}kg ` +
            `across ${Ragdoll.BodyPart.COUNT} parts`);
        return this;
    }

    /**
     * Rebuild the whole figure (dispose + create) at a position. Used for
     * scaling, respawning and re-posing - joints are recreated from
     * scratch so the body always stays fully connected.
     */
    rebuild(x = this.position.x, y = this.position.y, z = this.position.z,
        pose = this.currentPose) {
        const wasVisible = this.visible;
        this.dispose();
        this.create(x, y, z, pose);
        this.visible = true; // fresh colliders are live
        if (!wasVisible) this.setVisible(false);
        else this.setVisible(true);
        return this;
    }

    /**
     * Spawn the figure STANDING on the ground IN FRONT of the chair
     * (offset from the chair's bounding box along +X, the direction the
     * side-profile chair "faces"). Falls back to a spot near the origin
     * when there is no chair yet.
     * @param {Array<THREE.Mesh>} chairMeshes - the chair's meshes (pivot-local frame)
     * @param {number} groundY - ground plane height (world units)
     */
    spawnStandingInFront(chairMeshes = [], groundY = -5) {
        const box = chairLocalBox(chairMeshes);
        const chairMaxX = box.isEmpty() ? null : box.max.x;
        const centerZ = box.isEmpty() ? 0 : (box.min.z + box.max.z) / 2;

        // Build first, then snap by the figure's ACTUAL bounds: feet
        // exactly on the ground, and the figure's near edge a margin clear
        // of the chair's front face (never inside the chair or the floor)
        this.rebuild(0, this.standingPelvisY(groundY), 0, 'standing');
        const bounds = this.getBodyBounds();
        const dy = (groundY + 0.05) - bounds.minY;
        const targetMinX = chairMaxX !== null ? chairMaxX + 1.0 : 4;
        const dx = targetMinX - bounds.minX;
        const dz = centerZ - (bounds.minZ + bounds.maxZ) / 2;
        this.translateAll(dx, dy, dz);

        // Stand until touched (asleep bodies ignore gravity; grabs and
        // collisions wake them)
        this.sleepAll();

        console.log(`Figure standing in front of the chair: x ${(bounds.minX + dx).toFixed(1)}..${(bounds.maxX + dx).toFixed(1)}, feet at ${(bounds.minY + dy).toFixed(2)}`);
        return this;
    }

    /**
     * Re-pose the figure IN PLACE ('standing' | 'seated'): rebuild in the
     * new pose at its current x/z spot, then drop its lowest point back to
     * the ground. Independent of the chair - the seated pose just folds as
     * if perched on an invisible stool. Stays asleep so it holds the pose.
     */
    setPose(poseName, groundY = -5) {
        const { x, z } = this.position;
        this.rebuild(x, 0, z, poseName);
        const bounds = this.getBodyBounds();
        this.translateAll(0, (groundY + 0.05) - bounds.minY, 0);
        this.sleepAll();
        this.isSitting = (poseName === 'seated');
        this.currentPose = poseName;
        return this;
    }

    /**
     * Create a single body part at a world position/orientation.
     * @param {number} partId
     * @param {THREE.Vector3} pos
     * @param {THREE.Quaternion} quat
     */
    createBodyPart(partId, pos, quat) {
        const s = this.scale;
        const shape = this.partShapes[partId];

        // Rigid body carries BOTH translation and rotation (the old code
        // only rotated the display mesh - foot colliders stood vertical
        // while their meshes lay flat)
        const bodyDesc = this.RAPIER.RigidBodyDesc.dynamic()
            .setTranslation(pos.x, pos.y, pos.z)
            .setRotation({ x: quat.x, y: quat.y, z: quat.z, w: quat.w })
            .setLinearDamping(0.5)  // damping reduces oscillations
            .setAngularDamping(1.2)
            .setCcdEnabled(true);   // fast drops must not tunnel the thin ground/panels

        const body = this.world.createRigidBody(bodyDesc);

        // Collision groups: the WHOLE figure shares one membership bit and
        // filters that bit out, so body parts never collide with EACH
        // OTHER (they still collide with the chair/ground, whose default
        // groups include every bit). Whole-figure filtering is the stable
        // ragdoll setup: with anatomical proportions the capsules overlap
        // at every joint and self-contacts fight the joint solver - the
        // classic self-intersection + jitter failure mode.
        const FIGURE_BIT = 1 << 15;
        const membershipGroup = FIGURE_BIT;
        const filterGroups = 0xFFFF & ~FIGURE_BIT; // everything except ourselves
        const collisionGroups = (filterGroups << 16) | membershipGroup;

        // Anthropometric mass distribution (see massFractions). Total
        // mass follows the age curve ~height^2.3: 60kg adult at scale 1,
        // ~12kg toddler at 0.5, ~3.5kg newborn at 0.3 - a shrunken
        // figure must not hit the chair with adult momentum.
        const effectiveTotal = this.totalMass * Math.pow(this.scale, 2.3);
        const mass = effectiveTotal * (this.massFractions[partId] ?? (1 / Ragdoll.BodyPart.COUNT));

        const colliderDesc = (shape.type === 'box'
            ? this.RAPIER.ColliderDesc.cuboid(shape.hx * s, shape.hy * s, shape.hz * s)
            : this.RAPIER.ColliderDesc.capsule(shape.len / 2 * s, shape.r * s))
            .setMass(mass)
            .setRestitution(0.1)
            .setFriction(0.8)
            .setCollisionGroups(collisionGroups);

        const collider = this.world.createCollider(colliderDesc, body);

        this.colliders[partId] = collider;
        this.bodies[partId] = body;

        // Display mesh - ONE flat accent color for the whole figure
        const geometry = shape.type === 'box'
            ? new THREE.BoxGeometry(shape.hx * 2 * s, shape.hy * 2 * s, shape.hz * 2 * s)
            : new THREE.CapsuleGeometry(shape.r * s, shape.len * s, 8, 16);
        const material = new THREE.MeshStandardMaterial({
            color: FIGURE_COLOR,
            roughness: 0.85,
            metalness: 0.0
        });

        const mesh = new THREE.Mesh(geometry, material);
        mesh.position.copy(pos);
        mesh.quaternion.copy(quat);
        mesh.userData.ragdollPart = partId;

        this.scene.add(mesh);
        this.meshes[partId] = mesh;

        // Track body in physics world
        this.physicsWorld.bodies.set(mesh, body);
    }

    /**
     * Create all joints from the skeleton: revolute about +Z with
     * anatomical limits, plus a weak "muscle tone" position motor pulling
     * toward the pose's rest angle so the figure drapes like a relaxed
     * person instead of a cut-string marionette. Motors are deliberately
     * weak - gravity still wins.
     */
    createJoints(pose = 'standing') {
        const s = this.scale;
        for (const j of this.SKELETON) {
            const body1 = this.bodies[j.parent];
            const body2 = this.bodies[j.child];
            if (!body1 || !body2) {
                console.warn(`Cannot create joint ${j.name}: missing bodies`);
                continue;
            }
            const pa = j.p(), ca = j.c();
            const params = this.RAPIER.JointData.revolute(
                { x: pa[0] * s, y: pa[1] * s, z: pa[2] * s },
                { x: ca[0] * s, y: ca[1] * s, z: ca[2] * s },
                Z_AXIS
            );
            const joint = this.world.createImpulseJoint(params, body1, body2, true);
            // LIMITS MUST BE SET ON THE JOINT, NOT THE JointData: rapier
            // 0.17's JointData.limits/limitsEnabled fields are silently
            // ignored for impulse joints (verified live - every joint
            // reported limitsEnabled()===false and the neck sat at -2.0rad
            // against a -0.2 limit). setLimits() is what actually works.
            joint.setLimits(j.limits[0], j.limits[1]);
            // Belt and braces on top of the whole-figure collision groups
            joint.setContactsEnabled?.(false);
            // Muscle tone toward the pose's rest angle
            const rest = j.rest[pose] ?? 0;
            joint.configureMotorPosition?.(rest, j.tone[0], j.tone[1]);
            this.joints.push(joint);
        }
    }

    /**
     * Update ragdoll visualization
     */
    update() {
        if (!this.visible) return;

        // Sync mesh positions with physics bodies
        for (let i = 0; i < Ragdoll.BodyPart.COUNT; i++) {
            if (this.bodies[i] && this.meshes[i]) {
                const translation = this.bodies[i].translation();
                const rotation = this.bodies[i].rotation();

                this.meshes[i].position.set(translation.x, translation.y, translation.z);
                this.meshes[i].quaternion.set(rotation.x, rotation.y, rotation.z, rotation.w);
            }
        }
    }

    /**
     * Set visibility and disable physics interactions when hidden
     */
    setVisible(visible) {
        this.visible = visible;

        // Update mesh visibility
        this.meshes.forEach(mesh => {
            if (mesh) mesh.visible = visible;
        });

        // Enable/disable physics interactions
        this.colliders.forEach((collider, index) => {
            if (collider) {
                if (visible) {
                    // Restore saved collision groups (enable physics)
                    if (this.savedCollisionGroups[index] !== undefined) {
                        collider.setCollisionGroups(this.savedCollisionGroups[index]);
                        this.savedCollisionGroups[index] = undefined;
                    }
                } else if (this.savedCollisionGroups[index] === undefined) {
                    // Save current collision groups before disabling
                    this.savedCollisionGroups[index] = collider.collisionGroups();

                    // Disable collisions by setting filter to 0
                    const membership = this.savedCollisionGroups[index] & 0xFFFF;
                    collider.setCollisionGroups(membership); // Filter = 0, won't collide with anything
                }
            }
        });
    }

    /**
     * Reset to the stored position: full rebuild (bodies, colliders and
     * joints recreated in the pose layout).
     */
    reset() {
        this.rebuild();
    }

    /**
     * Check if ragdoll is stable (sitting properly)
     */
    isStable() {
        // Check if pelvis is relatively stationary
        if (!this.bodies[Ragdoll.BodyPart.PELVIS]) return false;

        const pelvisVel = this.bodies[Ragdoll.BodyPart.PELVIS].linvel();
        const speed = Math.sqrt(pelvisVel.x ** 2 + pelvisVel.y ** 2 + pelvisVel.z ** 2);

        // Considered stable if moving slowly
        return speed < 0.1;
    }

    /**
     * Get pelvis height (for chair contact detection)
     */
    getPelvisHeight() {
        if (!this.bodies[Ragdoll.BodyPart.PELVIS]) return 0;
        return this.bodies[Ragdoll.BodyPart.PELVIS].translation().y;
    }

    /**
     * Find the chair's SEAT height by casting rays straight down through
     * the middle of the chair's bounding box (physics colliders live in
     * the same pivot-local frame). The figure's own colliders are
     * excluded via the FIGURE collision bit. Falls back to 45% of the
     * chair's height when nothing sensible is hit.
     * @returns {{seatY: number, seatX: number}|null}
     */
    _findSeat(box) {
        if (box.isEmpty()) return null;
        const h = box.max.y - box.min.y;
        const w = box.max.x - box.min.x;
        const cz = (box.min.z + box.max.z) / 2;
        const FIGURE_BIT = 1 << 15;
        // Ray "collides with" everything except the figure
        const rayGroups = ((0xFFFF & ~FIGURE_BIT) << 16) | 0xFFFF;

        const hits = [];
        for (let i = 0; i < 5; i++) {
            // sample across the middle 50% of the chair's depth
            const rx = box.min.x + w * (0.3 + 0.1 * i);
            const ray = new this.RAPIER.Ray(
                { x: rx, y: box.max.y + 1, z: cz }, { x: 0, y: -1, z: 0 });
            const hit = this.world.castRay(ray, h + 2, true, undefined, rayGroups);
            if (!hit) continue;
            const y = box.max.y + 1 - hit.timeOfImpact;
            // keep plausible seat surfaces (not the floor, not the backrest top)
            if (y > box.min.y + 0.1 * h && y < box.min.y + 0.8 * h) {
                hits.push({ x: rx, y });
            }
        }
        if (hits.length === 0) {
            return { seatY: box.min.y + 0.45 * h, seatX: (box.min.x + box.max.x) / 2 };
        }
        // median height of the plausible hits = the seat surface
        hits.sort((a, b) => a.y - b.y);
        const median = hits[Math.floor(hits.length / 2)];
        return { seatY: median.y, seatX: hits.reduce((s2, p) => s2 + p.x, 0) / hits.length };
    }

    /**
     * Does any figure collider currently overlap a CHAIR collider?
     * Uses the query pipeline (no physics step needed); only colliders
     * belonging to the chair's compound bodies count - ground contact
     * and self-overlap are ignored.
     */
    _intersectsChair() {
        const chairBodies = new Set(
            (this.physicsWorld.compoundBodies || []).map(cb => cb.body.handle));
        if (chairBodies.size === 0) return false;
        // The query structures only refresh on world.step() - without
        // this, freshly built/translated colliders test against stale
        // positions and the whole check silently no-ops
        this.world.updateSceneQueries();
        for (let i = 0; i < Ragdoll.BodyPart.COUNT; i++) {
            const body = this.bodies[i], collider = this.colliders[i];
            if (!body || !collider) continue;
            let hit = false;
            this.world.intersectionsWithShape(
                body.translation(), body.rotation(), collider.shape,
                () => { hit = true; return false; }, // stop at first hit
                undefined, undefined, undefined, undefined,
                (other) => other.parent() && chairBodies.has(other.parent().handle));
            if (hit) return true;
        }
        return false;
    }

    /**
     * Translate the figure forward (+X) in small steps until it no
     * longer overlaps the chair. Returns the distance moved.
     */
    _pushClearOfChair(step = 0.25, maxDistance = 12) {
        let moved = 0;
        while (moved < maxDistance && this._intersectsChair()) {
            this.translateAll(step, 0, 0);
            moved += step;
        }
        return moved;
    }

    /**
     * Position ragdoll on chair: build the figure PRE-POSED SEATED (hips
     * and knees ~90deg) with the pelvis a small clearance above the
     * detected seat surface, so it drapes into the chair instead of
     * being dropped standing and crumpling.
     * @param {Array<THREE.Mesh>} chairMeshes - Array of chair mesh pieces
     * @param {number} groundY - ground plane height (world units)
     */
    positionOnChair(chairMeshes, groundY = -5) {
        if (!chairMeshes || chairMeshes.length === 0) {
            console.warn('No chair meshes provided for ragdoll positioning');
            return;
        }

        const box = chairLocalBox(chairMeshes);
        if (box.isEmpty()) return;
        const seat = this._findSeat(box);
        const cz = (box.min.z + box.max.z) / 2;

        // Pelvis bottom a small clearance above the seat (a 60kg dead-drop
        // launches an 8kg chair)
        const P = Ragdoll.BodyPart;
        const pelvisHalf = (this.partShapes[P.PELVIS].len / 2 + this.partShapes[P.PELVIS].r) * this.scale;
        const pelvisY = seat.seatY + pelvisHalf + 0.3;

        // Low seats: a 90deg knee bend would put the feet UNDER the floor.
        // Do what a person does on a low lounger - EXTEND the legs. Solve
        // the knee angle by bisection on the FK pose (cheap - no physics)
        // so the lowest point sits exactly on the ground; the ankle
        // counter-rotates to keep the foot flat (hip + knee + ankle = 0).
        const joint = (name) => this.SKELETON.find(j => j.name === name);
        const hipRest = joint('hipL').rest.seated;
        const setLegs = (k) => {
            joint('kneeL').rest.seated = k;
            joint('kneeR').rest.seated = k;
            const a = Math.max(-0.5, Math.min(0.5, -(hipRest + k)));
            joint('ankleL').rest.seated = a;
            joint('ankleR').rest.seated = a;
        };
        const poseMinY = (k) => {
            setLegs(k);
            const T = this.computePose('seated');
            return pelvisY + this._boundsOf(i => T[i]).minY;
        };
        const groundTop = groundY + 0.05;
        const DEFAULT_KNEE = -1.45; // ~83deg bend (normal chair)
        if (poseMinY(DEFAULT_KNEE) < groundTop) {
            let lo = DEFAULT_KNEE, hi = 0; // extend toward straight
            for (let i = 0; i < 12; i++) {
                const mid = (lo + hi) / 2;
                if (poseMinY(mid) < groundTop) lo = mid; else hi = mid;
            }
            setLegs(hi);
        } else {
            setLegs(DEFAULT_KNEE);
        }

        this.rebuild(seat.seatX, pelvisY, cz, 'seated');
        this.isSitting = true;

        // Safety net: whatever the pose, never start inside the floor
        const bounds = this.getBodyBounds();
        const sink = groundTop - bounds.minY;
        if (sink > 0) this.translateAll(0, sink, 0);

        // Small figures seated at the chair's centre can spawn INSIDE the
        // backrest (their torso is much shallower than an adult's). Nudge
        // the figure forward (+X, the way the chair faces) until no body
        // part overlaps a chair collider.
        const moved = this._pushClearOfChair();
        if (moved > 0) console.log(`Nudged seated figure ${moved.toFixed(2)} forward to clear the chair`);

        console.log(`Seated figure on chair: seat at y=${seat.seatY.toFixed(2)}, ` +
            `pelvis at (${seat.seatX.toFixed(2)}, ${pelvisY.toFixed(2)}, ${cz.toFixed(2)})`);
    }

    /**
     * Test stability over time
     * Returns metrics about ragdoll stability
     * @param {number} duration - Time to test in seconds
     * @returns {Promise<Object>} Stability metrics
     */
    async testStability(duration = 3.0) {
        const startTime = performance.now();
        const samples = [];
        const sampleInterval = 0.1; // Sample every 100ms

        return new Promise((resolve) => {
            const checkStability = () => {
                const elapsed = (performance.now() - startTime) / 1000;

                if (elapsed >= duration) {
                    // Calculate final metrics
                    const avgSpeed = samples.reduce((sum, s) => sum + s.speed, 0) / samples.length;
                    const maxSpeed = Math.max(...samples.map(s => s.speed));
                    const heightChange = Math.abs(samples[samples.length - 1].height - samples[0].height);

                    const isStable = avgSpeed < 0.05 && maxSpeed < 0.2 && heightChange < 0.2;

                    resolve({
                        stable: isStable,
                        averageSpeed: avgSpeed,
                        maxSpeed: maxSpeed,
                        heightChange: heightChange,
                        samples: samples.length
                    });
                    return;
                }

                // Take sample
                if (this.bodies[Ragdoll.BodyPart.PELVIS]) {
                    const pelvisVel = this.bodies[Ragdoll.BodyPart.PELVIS].linvel();
                    const speed = Math.sqrt(pelvisVel.x ** 2 + pelvisVel.y ** 2 + pelvisVel.z ** 2);
                    const height = this.getPelvisHeight();

                    samples.push({ time: elapsed, speed, height });
                }

                // Continue sampling
                setTimeout(checkStability, sampleInterval * 1000);
            };

            checkStability();
        });
    }

    /**
     * Cleanup
     */
    dispose() {
        // Remove joints
        this.joints.forEach(joint => {
            if (joint) {
                this.world.removeImpulseJoint(joint, true);
            }
        });

        // Remove bodies
        this.bodies.forEach((body, index) => {
            if (body) {
                this.world.removeRigidBody(body);
                this.physicsWorld.bodies.delete(this.meshes[index]);
            }
        });

        // Remove meshes from scene
        this.meshes.forEach(mesh => {
            if (mesh) {
                this.scene.remove(mesh);
                if (mesh.geometry) mesh.geometry.dispose();
                if (mesh.material) mesh.material.dispose();
            }
        });

        this.bodies = [];
        this.meshes = [];
        this.joints = [];
        this.colliders = [];
    }
}
